import { LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { MarkNode } from '@lexical/mark';
import { getCSSFromStyleObject } from '@lexical/selection';
import { LexicalEditor, ParagraphNode, SerializedEditorState, TextNode, createEditor } from 'lexical';
import { getTextStylesFromBrandStyle, tryToGetBrandColorRefByHEX } from 'modules/BrandDefinition';
import { TextHorizontalAlignmentType } from 'const';
import * as Models from 'models';
import { CustomListItemNode } from '../nodes/CustomListItemNode';
import { CustomListNode, SerializedCustomListNode } from '../nodes/CustomListNode';
import { CustomParagraphNode, SerializedCustomParagraphNode } from '../nodes/CustomParagraphNode';
import { NoWrapNode } from '../nodes/NoWrapNode';
import { ProjectMetadataTokenNode } from '../nodes/ProjectMetadataTokenNode';
import { ReferenceCitationNode } from '../nodes/ReferenceCitationNode';
import { ReferenceCitationSeparatorNode } from '../nodes/ReferenceCitationSeparatorNode';
import { SSIAnchorNode } from '../nodes/SSIAnchorNode';
import { getStyleObjectForBrandStyle } from '../Plugins/FontBrandStylePlugin/brandStyle';
import { getFormatTypeForBlockType } from '../Plugins/TextBlockPlugin/utils';
import { mainTheme } from '../Theme/mainTheme';

export const lexicalTextEditorConfig = {
  namespace: 'text-editor',
  editable: false,
  theme: mainTheme,
  onError: (error): void => {
    throw error;
  },
  nodes: [
    TextNode,
    CustomParagraphNode,
    {
      replace: ParagraphNode,
      with: (): CustomParagraphNode => new CustomParagraphNode(),
    },
    CustomListNode,
    {
      replace: ListNode,
      with: (node: ListNode): CustomListNode => new CustomListNode(node.__listType, node.__start),
    },
    CustomListItemNode,
    {
      replace: ListItemNode,
      with: (node: ListItemNode): CustomListItemNode => {
        return new CustomListItemNode(node.__value, node.__checked);
      },
      withKlass: CustomListItemNode,
    },
    MarkNode,
    LinkNode,
    ReferenceCitationNode,
    ReferenceCitationSeparatorNode,
    SSIAnchorNode,
    NoWrapNode,
    ProjectMetadataTokenNode,
  ],
};

export function createLexicalTextEditor(
  maybeStringifiedEditorState?: string | SerializedEditorState,
): LexicalEditor {
  const editor = createEditor(lexicalTextEditorConfig);

  if (maybeStringifiedEditorState) {
    editor.setEditorState(editor.parseEditorState(maybeStringifiedEditorState));
  }

  return editor;
}

export function createLexicalTextEditorEmptyState(
  brandStyle: Models.BrandStyleMap | undefined,
  colors: Models.BrandColorsList,
  fonts: Models.BrandFontsList,
): string {
  const textStyle = getCSSFromStyleObject(getStyleObjectForBrandStyle(brandStyle, colors, fonts));
  const styles = getTextStylesFromBrandStyle(brandStyle, colors, fonts);
  const textAlign = styles[Models.TextBrandStyleField.TEXT_ALIGN];

  let child: SerializedCustomParagraphNode | SerializedCustomListNode;
  if (textAlign === TextHorizontalAlignmentType.UNORDERED_LIST) {
    const bulletColorHex = styles[Models.TextBrandStyleField.BULLET_COLOR];
    const bulletColor = bulletColorHex ? tryToGetBrandColorRefByHEX(colors, bulletColorHex) : undefined;
    // LINE_HEIGHT and FONT_SIZE should be passed to list items
    child = {
      children:[],
      direction: 'ltr',
      format: '',
      indent: 0,
      type: CustomListNode.getType(),
      listType: 'bullet',
      version: 1,
      textFormat: 0,
      textStyle,
      start: 0,
      tag: 'ul',
      color: bulletColor,
    };
  } else {
    child = {
      children:[],
      direction: 'ltr',
      format: getFormatTypeForBlockType(textAlign) ?? '',
      indent: 0,
      type: CustomParagraphNode.getType(),
      version: 1,
      textFormat: 0,
      textStyle,
      lineHeight: styles[Models.TextBrandStyleField.LINE_HEIGHT],
      fontSize: styles[Models.TextBrandStyleField.FONT_SIZE],
    };
  }

  const state: SerializedEditorState = {
    root: {
      children: [child],
      direction: 'ltr',
      indent: 0,
      type: 'root',
      format: '',
      version: 1,
    },
  };

  return JSON.stringify(state);
}
